import React, { useState, useEffect, useCallback } from 'react';
import { useCRM } from '../context/CRMContext';
import {
  collection, query, where, orderBy, onSnapshot,
  addDoc, deleteDoc, doc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase/config';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getTestURL = (id) => window.location.origin + '/assess/' + id;

const STATUS_CONFIG = {
  pending:   { label: 'Pending',   color: '#f59e0b', bg: 'rgba(245,158,11,0.1)',  icon: '⏳' },
  completed: { label: 'Completed', color: '#10b981', bg: 'rgba(16,185,129,0.1)',  icon: '✅' },
};

const formatDate = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' });
};

// ─── Create Assessment Modal ──────────────────────────────────────────────────
function CreateModal({ leads, webForms, onClose, onCreate, companyId }) {
  const [leadId,   setLeadId]   = useState('');
  const [formId,   setFormId]   = useState('');
  const [note,     setNote]     = useState('');
  const [loading,  setLoading]  = useState(false);
  const [search,   setSearch]   = useState('');

  const filteredLeads = leads.filter(l => {
    const q = search.toLowerCase();
    return !q || l.name?.toLowerCase().includes(q) || l.phone?.includes(q) || l.email?.toLowerCase().includes(q);
  }).slice(0, 50);

  const handleCreate = async () => {
    if (!leadId) return;
    setLoading(true);
    const lead = leads.find(l => l.id === leadId);
    try {
      const ref = await addDoc(collection(db, 'assessments'), {
        companyId,
        leadId,
        leadName:  lead?.name || 'Unknown',
        leadEmail: lead?.email || '',
        leadPhone: lead?.phone || '',
        linkedFormId: formId || null,
        counsellorNote: note,
        status: 'pending',
        createdAt: serverTimestamp(),
        completedAt: null,
        responses: null,
        scores: null,
        report: null,
      });
      onCreate && onCreate(ref.id, lead);
      onClose();
    } catch (e) {
      console.error(e);
      alert('Failed to create assessment: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ position:'fixed', inset:0, zIndex:1000, background:'rgba(15,23,42,0.5)',
      backdropFilter:'blur(4px)', display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div style={{ background:'#fff', borderRadius:20, padding:32, width:'100%', maxWidth:500,
        boxShadow:'0 24px 80px rgba(0,0,0,0.2)' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
          <div>
            <h2 style={{ fontSize:18, fontWeight:800, color:'#0f172a', margin:0 }}>New Assessment</h2>
            <p style={{ fontSize:12, color:'#64748b', margin:'4px 0 0' }}>
              Create a personalised psychometric assessment for a lead
            </p>
          </div>
          <button onClick={onClose}
            style={{ width:32, height:32, borderRadius:8, border:'1px solid rgba(0,0,0,0.1)',
              background:'rgba(0,0,0,0.03)', cursor:'pointer', fontSize:16, color:'#64748b' }}>
            ×
          </button>
        </div>

        {/* Lead selector */}
        <div style={{ marginBottom:18 }}>
          <label style={{ fontSize:12, fontWeight:700, color:'#374151', textTransform:'uppercase',
            letterSpacing:0.5, display:'block', marginBottom:8 }}>Select Lead *</label>
          <input className="crm-input" value={search}
            onChange={e => { setSearch(e.target.value); setLeadId(''); }}
            placeholder="Search by name, phone or email…"
            style={{ width:'100%', marginBottom:8 }} />
          <div style={{ maxHeight:180, overflowY:'auto', border:'1px solid rgba(0,0,0,0.08)',
            borderRadius:10, background:'#fafafa' }}>
            {filteredLeads.length === 0 ? (
              <div style={{ padding:'12px 16px', fontSize:13, color:'#94a3b8' }}>No leads found</div>
            ) : filteredLeads.map(l => (
              <div key={l.id} onClick={() => { setLeadId(l.id); setSearch(l.name || ''); }}
                style={{
                  padding:'10px 14px', cursor:'pointer', fontSize:13,
                  background: leadId === l.id ? 'rgba(124,58,237,0.07)' : 'transparent',
                  borderLeft: leadId === l.id ? '3px solid #7c3aed' : '3px solid transparent',
                  transition:'all 0.1s',
                }}>
                <div style={{ fontWeight:600, color:'#0f172a' }}>{l.name || 'Unnamed'}</div>
                <div style={{ fontSize:11, color:'#94a3b8', marginTop:1 }}>
                  {[l.phone, l.email].filter(Boolean).join(' · ')}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Link to web form (optional) */}
        {webForms && webForms.length > 0 && (
          <div style={{ marginBottom:18 }}>
            <label style={{ fontSize:12, fontWeight:700, color:'#374151', textTransform:'uppercase',
              letterSpacing:0.5, display:'block', marginBottom:8 }}>Link to Web Form (optional)</label>
            <select className="crm-input" value={formId} onChange={e => setFormId(e.target.value)}
              style={{ width:'100%' }}>
              <option value="">— None —</option>
              {webForms.map(f => <option key={f.id} value={f.id}>{f.title}</option>)}
            </select>
            <div style={{ fontSize:11, color:'#94a3b8', marginTop:5 }}>
              The assessment link will be shown at the end of the selected form.
            </div>
          </div>
        )}

        {/* Counsellor note */}
        <div style={{ marginBottom:24 }}>
          <label style={{ fontSize:12, fontWeight:700, color:'#374151', textTransform:'uppercase',
            letterSpacing:0.5, display:'block', marginBottom:8 }}>Internal Note (optional)</label>
          <textarea className="crm-input" value={note} onChange={e => setNote(e.target.value)}
            placeholder="e.g. Grade 11 student, interested in engineering and medicine…"
            rows={2} style={{ width:'100%', resize:'vertical' }} />
        </div>

        <div style={{ display:'flex', gap:12 }}>
          <button onClick={onClose}
            style={{ flex:1, padding:'12px 0', borderRadius:12, border:'1.5px solid rgba(0,0,0,0.1)',
              background:'transparent', color:'#374151', cursor:'pointer', fontSize:14, fontWeight:600 }}>
            Cancel
          </button>
          <button onClick={handleCreate} disabled={!leadId || loading}
            style={{ flex:2, padding:'12px 0', borderRadius:12, border:'none',
              background: leadId ? 'linear-gradient(135deg,#7c3aed,#3b82f6)' : 'rgba(0,0,0,0.07)',
              color: leadId ? '#fff' : '#94a3b8', cursor: leadId ? 'pointer' : 'default',
              fontSize:14, fontWeight:700,
              boxShadow: leadId ? '0 4px 16px rgba(124,58,237,0.35)' : 'none' }}>
            {loading ? 'Creating…' : '🚀 Create & Get Link'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Share Modal ──────────────────────────────────────────────────────────────
function ShareModal({ assessment, onClose }) {
  const [copied, setCopied] = useState(false);
  const url = getTestURL(assessment.id);

  const copy = async () => {
    try { await navigator.clipboard.writeText(url); } catch { /* fallback */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const embedCode = '<iframe src="' + url + '" width="100%" height="700" frameborder="0" style="border-radius:16px;"></iframe>';

  return (
    <div style={{ position:'fixed', inset:0, zIndex:1000, background:'rgba(15,23,42,0.5)',
      backdropFilter:'blur(4px)', display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div style={{ background:'#fff', borderRadius:20, padding:32, width:'100%', maxWidth:520,
        boxShadow:'0 24px 80px rgba(0,0,0,0.2)' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
          <div>
            <h2 style={{ fontSize:18, fontWeight:800, color:'#0f172a', margin:0 }}>Share Assessment</h2>
            <p style={{ fontSize:12, color:'#64748b', margin:'4px 0 0' }}>For: {assessment.leadName}</p>
          </div>
          <button onClick={onClose}
            style={{ width:32, height:32, borderRadius:8, border:'1px solid rgba(0,0,0,0.1)',
              background:'rgba(0,0,0,0.03)', cursor:'pointer', fontSize:16, color:'#64748b' }}>×</button>
        </div>

        <div style={{ marginBottom:16 }}>
          <div style={{ fontSize:12, fontWeight:700, color:'#374151', textTransform:'uppercase',
            letterSpacing:0.5, marginBottom:8 }}>Direct Link</div>
          <div style={{ display:'flex', gap:8 }}>
            <input readOnly value={url}
              style={{ flex:1, padding:'10px 12px', borderRadius:10, border:'1px solid rgba(0,0,0,0.1)',
                fontSize:12, color:'#374151', background:'#f8fafc', fontFamily:'monospace' }} />
            <button onClick={copy}
              style={{ padding:'10px 16px', borderRadius:10, border:'none',
                background: copied ? '#10b981' : '#7c3aed', color:'#fff', cursor:'pointer',
                fontSize:12, fontWeight:700, whiteSpace:'nowrap', transition:'background 0.2s' }}>
              {copied ? '✓ Copied!' : 'Copy'}
            </button>
          </div>
        </div>

        <div style={{ marginBottom:20 }}>
          <div style={{ fontSize:12, fontWeight:700, color:'#374151', textTransform:'uppercase',
            letterSpacing:0.5, marginBottom:8 }}>Embed Code</div>
          <textarea readOnly value={embedCode} rows={3}
            onClick={e => e.target.select()}
            style={{ width:'100%', padding:'10px 12px', borderRadius:10,
              border:'1px solid rgba(0,0,0,0.1)', fontSize:11, color:'#64748b',
              background:'#f8fafc', fontFamily:'monospace', resize:'none' }} />
        </div>

        {assessment.linkedFormId && (
          <div style={{ padding:'12px 16px', borderRadius:10, marginBottom:20,
            background:'rgba(59,130,246,0.06)', border:'1px solid rgba(59,130,246,0.15)',
            fontSize:12, color:'#1d4ed8' }}>
            📋 This assessment is linked to a web form — respondents will be directed to this test automatically.
          </div>
        )}

        <div style={{ padding:'12px 16px', borderRadius:10, marginBottom:16,
          background:'rgba(16,185,129,0.05)', border:'1px solid rgba(16,185,129,0.15)',
          fontSize:12, color:'#065f46', lineHeight:1.7 }}>
          <strong>Sharing tips:</strong><br />
          • Send via WhatsApp or Email directly to the student<br />
          • Embed in your website or web form<br />
          • The test is mobile-friendly and takes ~15–20 minutes
        </div>

        <button onClick={onClose}
          style={{ width:'100%', padding:'12px 0', borderRadius:12,
            border:'1.5px solid rgba(0,0,0,0.1)', background:'transparent',
            color:'#374151', cursor:'pointer', fontSize:14, fontWeight:600 }}>
          Done
        </button>
      </div>
    </div>
  );
}

// ─── Report Preview Modal ────────────────────────────────────────────────────
function ReportPreviewModal({ assessment, onClose }) {
  const { report } = assessment;
  if (!report) return null;

  return (
    <div style={{ position:'fixed', inset:0, zIndex:1000, background:'rgba(15,23,42,0.55)',
      backdropFilter:'blur(4px)', display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div style={{ background:'#fff', borderRadius:20, width:'100%', maxWidth:560,
        maxHeight:'85vh', overflow:'hidden', display:'flex', flexDirection:'column',
        boxShadow:'0 24px 80px rgba(0,0,0,0.2)' }}>

        {/* Header */}
        <div style={{ padding:'20px 24px', borderBottom:'1px solid rgba(0,0,0,0.07)',
          display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <div style={{ fontSize:16, fontWeight:800, color:'#0f172a' }}>{assessment.leadName}</div>
            <div style={{ fontSize:12, color:'#64748b', marginTop:2 }}>
              Completed {formatDate(assessment.completedAt)}
            </div>
          </div>
          <button onClick={onClose}
            style={{ width:32, height:32, borderRadius:8, border:'1px solid rgba(0,0,0,0.1)',
              background:'rgba(0,0,0,0.03)', cursor:'pointer', fontSize:16, color:'#64748b' }}>×</button>
        </div>

        {/* Scrollable body */}
        <div style={{ overflowY:'auto', padding:24, flex:1 }}>

          {/* Summary row */}
          <div style={{ display:'flex', gap:12, marginBottom:20, flexWrap:'wrap' }}>
            <div style={{ padding:'10px 16px', borderRadius:10, background:'rgba(124,58,237,0.07)',
              border:'1px solid rgba(124,58,237,0.15)', flex:1, minWidth:120 }}>
              <div style={{ fontSize:10, color:'#7c3aed', fontWeight:700, textTransform:'uppercase',
                letterSpacing:0.5, marginBottom:4 }}>Career Type</div>
              <div style={{ fontSize:14, fontWeight:800, color:'#0f172a' }}>{report.hollandTypeLabel}</div>
            </div>
            <div style={{ padding:'10px 16px', borderRadius:10, background:'rgba(16,185,129,0.07)',
              border:'1px solid rgba(16,185,129,0.15)', flex:1, minWidth:120 }}>
              <div style={{ fontSize:10, color:'#059669', fontWeight:700, textTransform:'uppercase',
                letterSpacing:0.5, marginBottom:4 }}>Archetype</div>
              <div style={{ fontSize:14, fontWeight:800, color:'#0f172a' }}>{report.personalityArchetype}</div>
            </div>
            <div style={{ padding:'10px 16px', borderRadius:10, background:'rgba(245,158,11,0.07)',
              border:'1px solid rgba(245,158,11,0.15)', flex:1, minWidth:100 }}>
              <div style={{ fontSize:10, color:'#d97706', fontWeight:700, textTransform:'uppercase',
                letterSpacing:0.5, marginBottom:4 }}>Score</div>
              <div style={{ fontSize:22, fontWeight:900, color:'#0f172a', lineHeight:1 }}>
                {report.overallScore}<span style={{ fontSize:12, color:'#94a3b8' }}>/99</span>
              </div>
            </div>
          </div>

          {/* Top careers */}
          <div style={{ marginBottom:20 }}>
            <div style={{ fontSize:12, fontWeight:700, color:'#374151', textTransform:'uppercase',
              letterSpacing:0.5, marginBottom:12 }}>🏆 Top Career Matches</div>
            {(report.topCareers || []).slice(0, 3).map((c, i) => (
              <div key={c.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 14px',
                borderRadius:10, marginBottom:8,
                background: i === 0 ? 'rgba(124,58,237,0.05)' : 'rgba(0,0,0,0.02)',
                border: i === 0 ? '1px solid rgba(124,58,237,0.18)' : '1px solid rgba(0,0,0,0.06)' }}>
                <span style={{ fontSize:20, flexShrink:0 }}>{c.icon}</span>
                <span style={{ flex:1, fontSize:13, fontWeight:600, color:'#0f172a' }}>{c.title}</span>
                <span style={{ fontSize:14, fontWeight:800,
                  color: i === 0 ? '#7c3aed' : '#10b981' }}>{c.matchScore}%</span>
              </div>
            ))}
          </div>

          {/* Aptitude snapshot */}
          {report.aptitudeTotal != null && (
            <div style={{ marginBottom:20 }}>
              <div style={{ fontSize:12, fontWeight:700, color:'#374151', textTransform:'uppercase',
                letterSpacing:0.5, marginBottom:12 }}>🧮 Aptitude Scores</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                {(report.aptitudeRanked || []).map(({ category, score }) => (
                  <div key={category} style={{ padding:'10px 14px', borderRadius:10,
                    background:'rgba(0,0,0,0.02)', border:'1px solid rgba(0,0,0,0.07)' }}>
                    <div style={{ fontSize:11, color:'#64748b', fontWeight:600, textTransform:'capitalize',
                      marginBottom:4 }}>{category}</div>
                    <div style={{ fontSize:16, fontWeight:800, color:'#0f172a' }}>{score}%</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* View full report CTA */}
          <a href={getTestURL(assessment.id)} target="_blank" rel="noopener noreferrer"
            style={{ display:'block', textAlign:'center', padding:'12px 0', borderRadius:12,
              background:'linear-gradient(135deg,#7c3aed,#3b82f6)', color:'#fff',
              textDecoration:'none', fontSize:14, fontWeight:700,
              boxShadow:'0 4px 16px rgba(124,58,237,0.3)' }}>
            View Full Report →
          </a>
        </div>
      </div>
    </div>
  );
}

// ─── Main Assessments Page ────────────────────────────────────────────────────
export default function Assessments() {
  const { leads, webForms, companyId } = useCRM();
  const [assessments,   setAssessments]   = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [showCreate,    setShowCreate]    = useState(false);
  const [shareItem,     setShareItem]     = useState(null);
  const [reportItem,    setReportItem]    = useState(null);
  const [newLink,       setNewLink]       = useState(null); // { id, name }
  const [filterStatus,  setFilterStatus]  = useState('all');
  const [search,        setSearch]        = useState('');
  const [newCopied,     setNewCopied]     = useState(false);

  useEffect(() => {
    const q = query(
      collection(db, 'assessments'),
      where('companyId', '==', companyId),
      orderBy('createdAt', 'desc')
    );
    const unsub = onSnapshot(q, snap => {
      setAssessments(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => setLoading(false));
    return unsub;
  }, [companyId]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this assessment? This cannot be undone.')) return;
    try { await deleteDoc(doc(db, 'assessments', id)); }
    catch (e) { alert('Delete failed: ' + e.message); }
  };

  const handleCreated = (id, lead) => {
    setNewLink({ id, name: lead?.name });
  };

  const copyNewLink = async () => {
    if (!newLink) return;
    try { await navigator.clipboard.writeText(getTestURL(newLink.id)); }
    catch { /* ignore */ }
    setNewCopied(true);
    setTimeout(() => setNewCopied(false), 2000);
  };

  const filtered = assessments.filter(a => {
    if (filterStatus !== 'all' && a.status !== filterStatus) return false;
    if (search) {
      const q = search.toLowerCase();
      return a.leadName?.toLowerCase().includes(q) || a.leadEmail?.toLowerCase().includes(q);
    }
    return true;
  });

  const stats = {
    total:     assessments.length,
    completed: assessments.filter(a => a.status === 'completed').length,
    pending:   assessments.filter(a => a.status === 'pending').length,
  };

  return (
    <div className="page-enter" style={{ height:'100%', overflowY:'auto', padding:'24px 28px' }}>

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between',
        marginBottom:24, flexWrap:'wrap', gap:16 }}>
        <div>
          <h1 style={{ fontSize:22, fontWeight:800, color:'#0f172a', margin:'0 0 4px' }}>
            🧠 Psychometric Assessments
          </h1>
          <p style={{ fontSize:13, color:'#64748b', margin:0 }}>
            Create and manage career intelligence assessments for your leads
          </p>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn-primary"
          style={{ display:'flex', alignItems:'center', gap:8, fontSize:14, padding:'12px 22px',
            borderRadius:12, fontWeight:700 }}>
          + New Assessment
        </button>
      </div>

      {/* ── New link banner ────────────────────────────────────────────────── */}
      {newLink && (
        <div style={{ marginBottom:20, padding:'16px 20px', borderRadius:14,
          background:'rgba(16,185,129,0.07)', border:'1px solid rgba(16,185,129,0.25)',
          display:'flex', alignItems:'center', gap:14, flexWrap:'wrap' }}>
          <span style={{ fontSize:20 }}>✅</span>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:13, fontWeight:700, color:'#065f46', marginBottom:4 }}>
              Assessment created for {newLink.name}!
            </div>
            <div style={{ fontSize:12, color:'#047857', fontFamily:'monospace' }}>
              {getTestURL(newLink.id)}
            </div>
          </div>
          <button onClick={copyNewLink}
            style={{ padding:'8px 16px', borderRadius:10, border:'none',
              background: newCopied ? '#10b981' : '#059669', color:'#fff',
              cursor:'pointer', fontSize:12, fontWeight:700, transition:'background 0.2s' }}>
            {newCopied ? '✓ Copied!' : 'Copy Link'}
          </button>
          <button onClick={() => setNewLink(null)}
            style={{ padding:'8px 12px', borderRadius:10, border:'1px solid rgba(0,0,0,0.1)',
              background:'transparent', cursor:'pointer', fontSize:12, color:'#374151' }}>
            Dismiss
          </button>
        </div>
      )}

      {/* ── Stats row ─────────────────────────────────────────────────────── */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:14, marginBottom:24 }}>
        {[
          { label:'Total', value: stats.total, color:'#7c3aed', bg:'rgba(124,58,237,0.07)', icon:'📊' },
          { label:'Completed', value: stats.completed, color:'#10b981', bg:'rgba(16,185,129,0.07)', icon:'✅' },
          { label:'Pending', value: stats.pending, color:'#f59e0b', bg:'rgba(245,158,11,0.07)', icon:'⏳' },
        ].map(s => (
          <div key={s.label} style={{ padding:'16px 20px', borderRadius:14,
            background:'#fff', border:'1px solid rgba(0,0,0,0.06)',
            boxShadow:'0 2px 12px rgba(0,0,0,0.04)' }}>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <div style={{ width:36, height:36, borderRadius:10, background:s.bg,
                display:'flex', alignItems:'center', justifyContent:'center', fontSize:18 }}>
                {s.icon}
              </div>
              <div>
                <div style={{ fontSize:24, fontWeight:900, color:s.color, lineHeight:1 }}>{s.value}</div>
                <div style={{ fontSize:11, color:'#64748b', marginTop:2, fontWeight:500 }}>{s.label}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Filters ───────────────────────────────────────────────────────── */}
      <div style={{ display:'flex', gap:12, marginBottom:20, flexWrap:'wrap' }}>
        <input className="crm-input" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search by name or email…"
          style={{ flex:1, minWidth:180 }} />
        {['all','pending','completed'].map(s => (
          <button key={s} onClick={() => setFilterStatus(s)}
            style={{ padding:'9px 18px', borderRadius:10, border:'none',
              background: filterStatus === s ? '#7c3aed' : 'rgba(0,0,0,0.05)',
              color: filterStatus === s ? '#fff' : '#374151',
              cursor:'pointer', fontSize:13, fontWeight:600, transition:'all 0.15s',
              textTransform:'capitalize' }}>
            {s === 'all' ? 'All' : STATUS_CONFIG[s]?.icon + ' ' + STATUS_CONFIG[s]?.label}
          </button>
        ))}
      </div>

      {/* ── Assessment List ──────────────────────────────────────────────── */}
      {loading ? (
        <div style={{ textAlign:'center', padding:'60px 0', color:'#94a3b8' }}>Loading assessments…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign:'center', padding:'80px 0' }}>
          <div style={{ fontSize:52, marginBottom:16 }}>🧠</div>
          <div style={{ fontSize:17, fontWeight:700, color:'#0f172a', marginBottom:8 }}>
            {assessments.length === 0 ? 'No assessments yet' : 'No results found'}
          </div>
          <div style={{ fontSize:13, color:'#64748b', marginBottom:24 }}>
            {assessments.length === 0
              ? 'Create your first psychometric assessment to get started.'
              : 'Try adjusting your search or filters.'}
          </div>
          {assessments.length === 0 && (
            <button onClick={() => setShowCreate(true)} className="btn-primary"
              style={{ fontSize:14, padding:'12px 28px', borderRadius:12 }}>
              + Create First Assessment
            </button>
          )}
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {filtered.map(a => {
            const statusCfg = STATUS_CONFIG[a.status] || STATUS_CONFIG.pending;
            return (
              <div key={a.id} style={{
                background:'#fff', borderRadius:14, padding:'16px 20px',
                border:'1px solid rgba(0,0,0,0.06)',
                boxShadow:'0 2px 12px rgba(0,0,0,0.04)',
                display:'flex', alignItems:'center', gap:16, flexWrap:'wrap',
              }}>
                {/* Avatar */}
                <div style={{ width:44, height:44, borderRadius:12, flexShrink:0,
                  background:'linear-gradient(135deg,#7c3aed18,#3b82f618)',
                  border:'1px solid rgba(124,58,237,0.15)',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontSize:18, fontWeight:800, color:'#7c3aed' }}>
                  {(a.leadName || '?').slice(0,1).toUpperCase()}
                </div>

                {/* Info */}
                <div style={{ flex:1, minWidth:160 }}>
                  <div style={{ fontSize:14, fontWeight:700, color:'#0f172a', marginBottom:3 }}>
                    {a.leadName}
                  </div>
                  <div style={{ fontSize:11, color:'#94a3b8' }}>
                    {[a.leadPhone, a.leadEmail].filter(Boolean).join(' · ')}
                  </div>
                  {a.counsellorNote && (
                    <div style={{ fontSize:11, color:'#64748b', marginTop:3,
                      fontStyle:'italic', overflow:'hidden', textOverflow:'ellipsis',
                      whiteSpace:'nowrap', maxWidth:280 }}>
                      "{a.counsellorNote}"
                    </div>
                  )}
                </div>

                {/* Dates */}
                <div style={{ fontSize:11, color:'#94a3b8', textAlign:'center', minWidth:90 }}>
                  <div style={{ fontWeight:500 }}>Created</div>
                  <div style={{ color:'#374151', marginTop:2 }}>{formatDate(a.createdAt)}</div>
                </div>

                {/* Status badge */}
                <div style={{ padding:'5px 12px', borderRadius:20,
                  background: statusCfg.bg, fontSize:12, fontWeight:700,
                  color: statusCfg.color, display:'flex', alignItems:'center', gap:5,
                  flexShrink:0 }}>
                  {statusCfg.icon} {statusCfg.label}
                </div>

                {/* Report score (if completed) */}
                {a.status === 'completed' && a.report?.overallScore && (
                  <div style={{ textAlign:'center', flexShrink:0 }}>
                    <div style={{ fontSize:20, fontWeight:900, color:'#7c3aed', lineHeight:1 }}>
                      {a.report.overallScore}
                    </div>
                    <div style={{ fontSize:9, color:'#94a3b8', fontWeight:600 }}>SCORE</div>
                  </div>
                )}

                {/* Actions */}
                <div style={{ display:'flex', gap:8, flexShrink:0 }}>
                  <button onClick={() => setShareItem(a)}
                    style={{ padding:'8px 14px', borderRadius:10, border:'none',
                      background:'rgba(124,58,237,0.07)', color:'#7c3aed',
                      cursor:'pointer', fontSize:12, fontWeight:700 }}>
                    🔗 Share
                  </button>
                  {a.status === 'completed' && (
                    <button onClick={() => setReportItem(a)}
                      style={{ padding:'8px 14px', borderRadius:10, border:'none',
                        background:'rgba(16,185,129,0.08)', color:'#059669',
                        cursor:'pointer', fontSize:12, fontWeight:700 }}>
                      📊 Report
                    </button>
                  )}
                  <button onClick={() => handleDelete(a.id)}
                    style={{ padding:'8px 12px', borderRadius:10, border:'none',
                      background:'rgba(239,68,68,0.07)', color:'#dc2626',
                      cursor:'pointer', fontSize:12 }}>
                    🗑
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Info footer ────────────────────────────────────────────────────── */}
      <div style={{ marginTop:32, padding:'20px 24px', borderRadius:16,
        background:'rgba(124,58,237,0.04)', border:'1px solid rgba(124,58,237,0.12)',
        display:'flex', gap:16, flexWrap:'wrap', alignItems:'flex-start' }}>
        <span style={{ fontSize:24, flexShrink:0 }}>💡</span>
        <div style={{ flex:1, fontSize:13, color:'#374151', lineHeight:1.7 }}>
          <strong>How it works:</strong> Create an assessment linked to a lead → Copy and share the link via
          WhatsApp or email → The student completes 68 questions in ~15–20 minutes →
          A comprehensive career intelligence report is automatically generated and available here.
          <br />
          <span style={{ color:'#7c3aed', fontWeight:500 }}>
            You can also embed the assessment in a web form under Web Forms → Advanced → Psychometric Test.
          </span>
        </div>
      </div>

      {/* ── Modals ──────────────────────────────────────────────────────────── */}
      {showCreate && (
        <CreateModal
          leads={leads || []}
          webForms={webForms || []}
          companyId={companyId}
          onClose={() => setShowCreate(false)}
          onCreate={handleCreated}
        />
      )}
      {shareItem && <ShareModal assessment={shareItem} onClose={() => setShareItem(null)} />}
      {reportItem && <ReportPreviewModal assessment={reportItem} onClose={() => setReportItem(null)} />}
    </div>
  );
}
